import type { FlipperPart, GuideRailPart, RampPart, TableDefinition, TargetPart, Vec2 } from './definition';

interface NormalizedPoint { x: number; y: number; }

interface LayoutProfile {
  centers: Record<string, NormalizedPoint>;
  mainRamp: NormalizedPoint[];
  mainExitVelocity: NormalizedPoint;
  upperRamp?: NormalizedPoint[];
  upperExitVelocity?: NormalizedPoint;
  orbitOuter: NormalizedPoint[];
  orbitInner: NormalizedPoint[];
  upperFlippers?: { left: NormalizedPoint; right: NormalizedPoint; lengthRatio: number };
}

const PROFILES: Record<string, LayoutProfile> = {
  'grand-circuit-v1': {
    centers: {
      'roll-a': { x: 0.29, y: 0.115 }, 'roll-b': { x: 0.45, y: 0.115 }, 'roll-c': { x: 0.61, y: 0.115 },
      'bumper-left': { x: 0.31, y: 0.25 }, 'bumper-right': { x: 0.57, y: 0.25 }, 'bumper-center': { x: 0.44, y: 0.36 },
      'target-1': { x: 0.29, y: 0.51 }, 'target-2': { x: 0.43, y: 0.49 }, 'target-3': { x: 0.57, y: 0.51 },
      'catch-grand': { x: 0.18, y: 0.18 }, 'insert-center': { x: 0.44, y: 0.43 }, 'insert-ramp': { x: 0.7, y: 0.69 },
      'insert-orbit': { x: 0.15, y: 0.58 },
    },
    mainRamp: [
      { x: 0.7, y: 0.66 }, { x: 0.78, y: 0.56 }, { x: 0.79, y: 0.43 },
      { x: 0.74, y: 0.31 }, { x: 0.64, y: 0.22 }, { x: 0.54, y: 0.18 },
    ],
    mainExitVelocity: { x: -0.14, y: 0.17 },
    orbitOuter: [{ x: 0.1, y: 0.61 }, { x: 0.08, y: 0.45 }, { x: 0.11, y: 0.29 }, { x: 0.18, y: 0.17 }, { x: 0.25, y: 0.09 }],
    orbitInner: [{ x: 0.18, y: 0.58 }, { x: 0.17, y: 0.44 }, { x: 0.2, y: 0.31 }, { x: 0.27, y: 0.2 }],
  },
  'candy-carnival-v1': {
    centers: {
      'roll-a': { x: 0.2, y: 0.14 }, 'roll-b': { x: 0.48, y: 0.1 }, 'roll-c': { x: 0.74, y: 0.15 },
      'bumper-left': { x: 0.22, y: 0.28 }, 'bumper-center': { x: 0.5, y: 0.38 }, 'bumper-right': { x: 0.78, y: 0.27 },
      'target-1': { x: 0.19, y: 0.55 }, 'target-2': { x: 0.5, y: 0.6 }, 'target-3': { x: 0.76, y: 0.53 },
      'catch-candy': { x: 0.3, y: 0.18 }, 'insert-center': { x: 0.5, y: 0.48 }, 'insert-ramp': { x: 0.63, y: 0.72 },
      'insert-orbit': { x: 0.13, y: 0.62 },
    },
    mainRamp: [
      { x: 0.63, y: 0.69 }, { x: 0.73, y: 0.62 }, { x: 0.78, y: 0.51 },
      { x: 0.72, y: 0.4 }, { x: 0.58, y: 0.3 }, { x: 0.38, y: 0.22 }, { x: 0.28, y: 0.16 },
    ],
    mainExitVelocity: { x: 0.12, y: 0.15 },
    orbitOuter: [{ x: 0.08, y: 0.67 }, { x: 0.06, y: 0.49 }, { x: 0.1, y: 0.31 }, { x: 0.19, y: 0.18 }, { x: 0.31, y: 0.1 }],
    orbitInner: [{ x: 0.18, y: 0.65 }, { x: 0.16, y: 0.5 }, { x: 0.2, y: 0.35 }, { x: 0.31, y: 0.23 }],
  },
  'cosmic-relay-v1': {
    centers: {
      'roll-a': { x: 0.22, y: 0.11 }, 'roll-b': { x: 0.5, y: 0.16 }, 'roll-c': { x: 0.72, y: 0.09 },
      'bumper-left': { x: 0.24, y: 0.42 }, 'bumper-center': { x: 0.48, y: 0.31 }, 'bumper-right': { x: 0.7, y: 0.21 },
      // Cosmic's relay bank deliberately zig-zags across the playfield instead
      // of reading as another shallow three-target row. Alpha begins low-left,
      // Beta forces a cross-table recovery to the right, and Gamma finishes in
      // the center lane beneath the reactor cluster. The mission order is
      // unchanged; the required aiming pattern is now physically distinctive.
      'target-1': { x: 0.22, y: 0.58 }, 'target-2': { x: 0.73, y: 0.5 }, 'target-3': { x: 0.48, y: 0.43 },
      'catch-cosmic': { x: 0.14, y: 0.23 }, 'insert-center': { x: 0.5, y: 0.4 }, 'insert-ramp': { x: 0.31, y: 0.7 },
      'insert-orbit': { x: 0.11, y: 0.55 },
    },
    mainRamp: [
      { x: 0.31, y: 0.67 }, { x: 0.22, y: 0.56 }, { x: 0.2, y: 0.43 },
      { x: 0.27, y: 0.3 }, { x: 0.43, y: 0.2 }, { x: 0.63, y: 0.14 }, { x: 0.76, y: 0.18 },
    ],
    mainExitVelocity: { x: -0.16, y: 0.17 },
    orbitOuter: [{ x: 0.08, y: 0.6 }, { x: 0.07, y: 0.42 }, { x: 0.13, y: 0.25 }, { x: 0.26, y: 0.12 }, { x: 0.42, y: 0.07 }],
    orbitInner: [{ x: 0.18, y: 0.59 }, { x: 0.17, y: 0.43 }, { x: 0.23, y: 0.29 }, { x: 0.36, y: 0.18 }],
  },
  'neon-rush-v1': {
    centers: {
      'roll-a': { x: 0.34, y: 0.1 }, 'roll-b': { x: 0.5, y: 0.14 }, 'roll-c': { x: 0.66, y: 0.1 },
      'bumper-left': { x: 0.38, y: 0.25 }, 'bumper-center': { x: 0.52, y: 0.36 }, 'bumper-right': { x: 0.64, y: 0.47 },
      'target-1': { x: 0.22, y: 0.45 }, 'target-2': { x: 0.3, y: 0.57 }, 'target-3': { x: 0.72, y: 0.58 },
      'catch-neon': { x: 0.16, y: 0.16 }, 'insert-center': { x: 0.53, y: 0.42 }, 'insert-ramp': { x: 0.74, y: 0.69 },
      'insert-orbit': { x: 0.12, y: 0.56 },
    },
    mainRamp: [
      { x: 0.74, y: 0.66 }, { x: 0.75, y: 0.54 }, { x: 0.72, y: 0.42 },
      { x: 0.67, y: 0.31 }, { x: 0.59, y: 0.21 }, { x: 0.51, y: 0.13 },
    ],
    mainExitVelocity: { x: -0.08, y: 0.22 },
    orbitOuter: [{ x: 0.1, y: 0.61 }, { x: 0.09, y: 0.47 }, { x: 0.12, y: 0.34 }, { x: 0.19, y: 0.23 }, { x: 0.29, y: 0.14 }],
    orbitInner: [{ x: 0.2, y: 0.59 }, { x: 0.19, y: 0.47 }, { x: 0.22, y: 0.36 }, { x: 0.29, y: 0.27 }],
  },
  'haunted-keep-v1': {
    centers: {
      'roll-a': { x: 0.2, y: 0.11 }, 'roll-b': { x: 0.5, y: 0.08 }, 'roll-c': { x: 0.78, y: 0.11 },
      'bumper-left': { x: 0.24, y: 0.2 }, 'bumper-center': { x: 0.5, y: 0.29 }, 'bumper-right': { x: 0.76, y: 0.2 },
      'target-1': { x: 0.29, y: 0.41 }, 'target-2': { x: 0.5, y: 0.37 }, 'target-3': { x: 0.71, y: 0.41 },
      'catch-haunted': { x: 0.14, y: 0.17 }, 'insert-center': { x: 0.5, y: 0.47 }, 'insert-ramp': { x: 0.76, y: 0.74 },
      'insert-orbit': { x: 0.12, y: 0.58 },
    },
    mainRamp: [
      { x: 0.76, y: 0.71 }, { x: 0.82, y: 0.62 }, { x: 0.8, y: 0.51 },
      { x: 0.73, y: 0.4 }, { x: 0.61, y: 0.3 }, { x: 0.52, y: 0.24 },
    ],
    mainExitVelocity: { x: -0.13, y: 0.17 },
    upperRamp: [
      { x: 0.27, y: 0.58 }, { x: 0.2, y: 0.49 }, { x: 0.21, y: 0.38 },
      { x: 0.31, y: 0.28 }, { x: 0.45, y: 0.21 }, { x: 0.58, y: 0.24 },
    ],
    upperExitVelocity: { x: 0.12, y: 0.15 },
    orbitOuter: [{ x: 0.08, y: 0.64 }, { x: 0.07, y: 0.47 }, { x: 0.1, y: 0.3 }, { x: 0.17, y: 0.17 }, { x: 0.27, y: 0.1 }],
    orbitInner: [{ x: 0.18, y: 0.61 }, { x: 0.17, y: 0.47 }, { x: 0.2, y: 0.34 }, { x: 0.28, y: 0.23 }],
    upperFlippers: { left: { x: 0.31, y: 0.59 }, right: { x: 0.69, y: 0.59 }, lengthRatio: 0.145 },
  },
  'jungle-temple-v1': {
    centers: {
      'roll-a': { x: 0.18, y: 0.12 }, 'roll-b': { x: 0.5, y: 0.09 }, 'roll-c': { x: 0.8, y: 0.12 },
      'bumper-left': { x: 0.25, y: 0.3 }, 'bumper-center': { x: 0.5, y: 0.22 }, 'bumper-right': { x: 0.75, y: 0.3 },
      'target-1': { x: 0.25, y: 0.49 }, 'target-2': { x: 0.5, y: 0.55 }, 'target-3': { x: 0.75, y: 0.49 },
      'catch-jungle': { x: 0.28, y: 0.16 }, 'insert-center': { x: 0.5, y: 0.42 }, 'insert-ramp': { x: 0.54, y: 0.7 },
      'insert-orbit': { x: 0.11, y: 0.61 },
    },
    mainRamp: [
      { x: 0.54, y: 0.67 }, { x: 0.62, y: 0.58 }, { x: 0.65, y: 0.46 },
      { x: 0.61, y: 0.34 }, { x: 0.51, y: 0.25 }, { x: 0.4, y: 0.19 },
    ],
    mainExitVelocity: { x: 0.13, y: 0.16 },
    orbitOuter: [{ x: 0.07, y: 0.65 }, { x: 0.06, y: 0.49 }, { x: 0.09, y: 0.33 }, { x: 0.17, y: 0.2 }, { x: 0.3, y: 0.11 }],
    orbitInner: [{ x: 0.17, y: 0.62 }, { x: 0.16, y: 0.49 }, { x: 0.2, y: 0.37 }, { x: 0.3, y: 0.27 }],
  },
  'deep-sea-dive-v1': {
    centers: {
      'roll-a': { x: 0.27, y: 0.1 }, 'roll-b': { x: 0.5, y: 0.13 }, 'roll-c': { x: 0.72, y: 0.1 },
      'bumper-left': { x: 0.22, y: 0.34 }, 'bumper-center': { x: 0.5, y: 0.29 }, 'bumper-right': { x: 0.76, y: 0.37 },
      'target-1': { x: 0.2, y: 0.52 }, 'target-2': { x: 0.5, y: 0.46 }, 'target-3': { x: 0.78, y: 0.54 },
      'catch-deep': { x: 0.3, y: 0.22 }, 'insert-center': { x: 0.5, y: 0.4 }, 'insert-ramp': { x: 0.67, y: 0.72 },
      'insert-orbit': { x: 0.12, y: 0.61 },
    },
    mainRamp: [
      { x: 0.67, y: 0.69 }, { x: 0.77, y: 0.6 }, { x: 0.8, y: 0.48 },
      { x: 0.74, y: 0.36 }, { x: 0.6, y: 0.26 }, { x: 0.42, y: 0.2 }, { x: 0.28, y: 0.22 },
    ],
    mainExitVelocity: { x: 0.15, y: 0.13 },
    orbitOuter: [{ x: 0.07, y: 0.68 }, { x: 0.05, y: 0.53 }, { x: 0.08, y: 0.37 }, { x: 0.16, y: 0.23 }, { x: 0.29, y: 0.13 }],
    orbitInner: [{ x: 0.18, y: 0.65 }, { x: 0.16, y: 0.52 }, { x: 0.2, y: 0.4 }, { x: 0.3, y: 0.3 }],
  },
  'clockwork-district-v1': {
    centers: {
      'roll-a': { x: 0.2, y: 0.1 }, 'roll-b': { x: 0.47, y: 0.14 }, 'roll-c': { x: 0.73, y: 0.09 },
      'bumper-left': { x: 0.24, y: 0.27 }, 'bumper-center': { x: 0.5, y: 0.38 }, 'bumper-right': { x: 0.72, y: 0.24 },
      'target-1': { x: 0.25, y: 0.51 }, 'target-2': { x: 0.5, y: 0.47 }, 'target-3': { x: 0.73, y: 0.56 },
      'catch-clockwork': { x: 0.17, y: 0.19 }, 'insert-center': { x: 0.5, y: 0.43 }, 'insert-ramp': { x: 0.73, y: 0.72 },
      'insert-orbit': { x: 0.13, y: 0.59 },
    },
    mainRamp: [
      { x: 0.73, y: 0.69 }, { x: 0.79, y: 0.58 }, { x: 0.76, y: 0.46 },
      { x: 0.65, y: 0.36 }, { x: 0.52, y: 0.28 }, { x: 0.43, y: 0.19 },
    ],
    mainExitVelocity: { x: -0.16, y: 0.17 },
    upperRamp: [
      { x: 0.3, y: 0.58 }, { x: 0.22, y: 0.49 }, { x: 0.24, y: 0.37 },
      { x: 0.35, y: 0.28 }, { x: 0.5, y: 0.23 }, { x: 0.61, y: 0.27 },
    ],
    upperExitVelocity: { x: 0.14, y: 0.16 },
    orbitOuter: [{ x: 0.09, y: 0.62 }, { x: 0.07, y: 0.46 }, { x: 0.12, y: 0.3 }, { x: 0.23, y: 0.18 }, { x: 0.35, y: 0.1 }],
    orbitInner: [{ x: 0.19, y: 0.6 }, { x: 0.18, y: 0.46 }, { x: 0.23, y: 0.34 }, { x: 0.34, y: 0.24 }],
  },
  'frontier-run-v1': {
    centers: {
      'roll-a': { x: 0.17, y: 0.12 }, 'roll-b': { x: 0.38, y: 0.09 }, 'roll-c': { x: 0.63, y: 0.12 },
      'bumper-left': { x: 0.2, y: 0.38 }, 'bumper-center': { x: 0.48, y: 0.32 }, 'bumper-right': { x: 0.75, y: 0.39 },
      'target-1': { x: 0.22, y: 0.22 }, 'target-2': { x: 0.5, y: 0.2 }, 'target-3': { x: 0.75, y: 0.23 },
      'catch-frontier': { x: 0.3, y: 0.16 }, 'insert-center': { x: 0.49, y: 0.48 }, 'insert-ramp': { x: 0.71, y: 0.71 },
      'insert-orbit': { x: 0.12, y: 0.61 },
    },
    mainRamp: [
      { x: 0.71, y: 0.68 }, { x: 0.78, y: 0.59 }, { x: 0.75, y: 0.48 },
      { x: 0.66, y: 0.39 }, { x: 0.55, y: 0.32 }, { x: 0.47, y: 0.25 },
    ],
    mainExitVelocity: { x: -0.13, y: 0.18 },
    upperRamp: [
      { x: 0.29, y: 0.62 }, { x: 0.2, y: 0.54 }, { x: 0.18, y: 0.42 },
      { x: 0.24, y: 0.31 }, { x: 0.36, y: 0.24 }, { x: 0.48, y: 0.25 },
    ],
    upperExitVelocity: { x: 0.16, y: 0.15 },
    orbitOuter: [{ x: 0.07, y: 0.67 }, { x: 0.06, y: 0.5 }, { x: 0.1, y: 0.34 }, { x: 0.19, y: 0.22 }, { x: 0.31, y: 0.13 }],
    orbitInner: [{ x: 0.17, y: 0.64 }, { x: 0.16, y: 0.5 }, { x: 0.2, y: 0.38 }, { x: 0.31, y: 0.28 }],
  },
  'dragon-keep-v1': {
    centers: {
      'roll-a': { x: 0.17, y: 0.11 }, 'roll-b': { x: 0.5, y: 0.08 }, 'roll-c': { x: 0.8, y: 0.11 },
      'bumper-left': { x: 0.23, y: 0.29 }, 'bumper-center': { x: 0.5, y: 0.2 }, 'bumper-right': { x: 0.76, y: 0.29 },
      'target-1': { x: 0.5, y: 0.39 }, 'target-2': { x: 0.5, y: 0.48 }, 'target-3': { x: 0.5, y: 0.57 },
      'catch-dragon': { x: 0.13, y: 0.15 }, 'insert-center': { x: 0.5, y: 0.34 }, 'insert-ramp': { x: 0.78, y: 0.73 },
      'insert-orbit': { x: 0.11, y: 0.59 },
    },
    mainRamp: [
      { x: 0.78, y: 0.7 }, { x: 0.84, y: 0.6 }, { x: 0.81, y: 0.48 },
      { x: 0.72, y: 0.37 }, { x: 0.61, y: 0.27 }, { x: 0.54, y: 0.18 },
    ],
    mainExitVelocity: { x: -0.12, y: 0.2 },
    upperRamp: [
      { x: 0.23, y: 0.61 }, { x: 0.16, y: 0.51 }, { x: 0.18, y: 0.39 },
      { x: 0.28, y: 0.29 }, { x: 0.41, y: 0.22 }, { x: 0.52, y: 0.19 },
    ],
    upperExitVelocity: { x: 0.13, y: 0.19 },
    orbitOuter: [{ x: 0.07, y: 0.64 }, { x: 0.06, y: 0.47 }, { x: 0.09, y: 0.3 }, { x: 0.17, y: 0.18 }, { x: 0.28, y: 0.1 }],
    orbitInner: [{ x: 0.17, y: 0.62 }, { x: 0.16, y: 0.47 }, { x: 0.2, y: 0.34 }, { x: 0.29, y: 0.24 }],
    upperFlippers: { left: { x: 0.29, y: 0.61 }, right: { x: 0.71, y: 0.61 }, lengthRatio: 0.15 },
  },
};

function point(table: TableDefinition, normalized: NormalizedPoint): Vec2 {
  return { x: table.board.width * normalized.x, y: table.board.height * normalized.y };
}

function setCenter(table: TableDefinition, id: string, normalized: NormalizedPoint): void {
  const part = table.parts.find((candidate) => candidate.id === id);
  if (part && 'center' in part) part.center = point(table, normalized);
}

function setRail(table: TableDefinition, id: string, points: NormalizedPoint[], collider: boolean): void {
  const rail = table.parts.find((part): part is GuideRailPart => part.id === id && part.kind === 'guide-rail');
  if (!rail) return;
  rail.points = points.map((candidate) => point(table, candidate));
  rail.collider = collider;
}

function setRamp(
  table: TableDefinition,
  id: string,
  path: NormalizedPoint[] | undefined,
  exitVelocity: NormalizedPoint | undefined,
): void {
  if (!path || path.length < 2) return;
  const ramp = table.parts.find((part): part is RampPart => part.id === id && part.kind === 'ramp');
  if (!ramp) return;
  ramp.path = path.map((candidate) => point(table, candidate));
  ramp.entry = { ...ramp.path[0]! };
  ramp.exit = { ...ramp.path[ramp.path.length - 1]! };
  if (exitVelocity) {
    ramp.exitVelocity = {
      x: table.board.width * exitVelocity.x,
      y: table.board.height * exitVelocity.y,
    };
  }
}

function setUpperFlippers(table: TableDefinition, profile: LayoutProfile): void {
  if (!profile.upperFlippers) return;
  const left = table.parts.find((part): part is FlipperPart => part.id === 'flip-upper-l' && part.kind === 'flipper-left');
  const right = table.parts.find((part): part is FlipperPart => part.id === 'flip-upper-r' && part.kind === 'flipper-right');
  const length = Math.max(7.5, table.board.width * profile.upperFlippers.lengthRatio);
  if (left) { left.pivot = point(table, profile.upperFlippers.left); left.length = length; }
  if (right) { right.pivot = point(table, profile.upperFlippers.right); right.length = length; }
}

function applyCosmicRelayNodes(table: TableDefinition): void {
  if (table.id !== 'cosmic-relay-v1') return;

  const specs = [
    { id: 'target-1', widthRatio: 0.11, scoreValue: 900, destroyScoreValue: 1200, appearsAfterPartId: 'catch-cosmic' },
    { id: 'target-2', widthRatio: 0.07, scoreValue: 1300, destroyScoreValue: 2200, appearsAfterPartId: 'target-1' },
    { id: 'target-3', widthRatio: 0.085, scoreValue: 1800, destroyScoreValue: 3600, appearsAfterPartId: 'target-2' },
  ] as const;

  for (const spec of specs) {
    const relay = table.parts.find(
      (part): part is TargetPart => part.id === spec.id && part.kind === 'target',
    );
    if (!relay) continue;
    relay.width = table.board.width * spec.widthRatio;
    relay.dropDown = true;
    relay.hitPoints = 1;
    relay.scoreValue = spec.scoreValue;
    relay.destroyScoreValue = spec.destroyScoreValue;
    relay.appearsAfterPartId = spec.appearsAfterPartId;
  }
}

/**
 * Final table-specific layout pass for official machines.
 *
 * Built-ins share a safe lower hardware foundation, but their upper playfields
 * should not read like the same machine stretched to different dimensions.
 * This pass moves the actual scoring mechanisms and ramp routes, not just art.
 * Internal divider/inner-orbit colliders are opened up deliberately: they stay
 * visible as rails but no longer form narrow pinching pockets for slow balls.
 */
export function applyBuiltInLayoutIdentity(table: TableDefinition): TableDefinition {
  const profile = PROFILES[table.id];
  if (!profile) return table;

  for (const [id, normalized] of Object.entries(profile.centers)) setCenter(table, id, normalized);
  setRamp(table, 'ramp-main', profile.mainRamp, profile.mainExitVelocity);
  setRamp(table, 'ramp-upper-loop', profile.upperRamp, profile.upperExitVelocity);
  setRail(table, 'left-orbit-outer', profile.orbitOuter, true);
  setRail(table, 'left-orbit-inner', profile.orbitInner, false);
  setUpperFlippers(table, profile);
  applyCosmicRelayNodes(table);

  // The lane dividers remain visually useful, but removing these two small
  // colliders avoids the most common top-lane wedge geometry on slow rebounds.
  for (const id of ['top-divider-left', 'top-divider-right']) {
    const rail = table.parts.find((part): part is GuideRailPart => part.id === id && part.kind === 'guide-rail');
    if (rail) rail.collider = false;
  }

  return table;
}
